// StoryOfDayCard — surfaces one anonymized member story when the
// moderation queue has approved content. Replaces leaderboard
// comparison pressure with quiet connection.
//
// Reaction is a single tap. We don't tell the user "your reaction was
// recorded" — there's no leaderboard, no notification. The point is
// to feel less alone, not to score.

import { useState } from 'react';
import { Heart } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Icon } from '../../components/ui/Icon';
import { useApi } from '../../hooks/useApi';
import { apiPost } from '../../lib/api';

interface StoryWire {
  id: number;
  pseudonym: string;
  dayIndex: number | null;
  body: string;
  reactions: number;
}

export function StoryOfDayCard() {
  const res = useApi<StoryWire | null>('/api/community/story-of-day');
  const [bumped, setBumped] = useState(false);
  const [localReactions, setLocalReactions] = useState<number | null>(null);

  if (res.status !== 'ok' || !res.data) return null;
  const story = res.data;
  const reactions = localReactions ?? story.reactions;

  async function react() {
    if (bumped) return;
    setBumped(true);
    setLocalReactions(reactions + 1);
    try {
      await apiPost('/api/community/react', { storyId: story.id });
    } catch {
      // Soft signal — quiet failure.
    }
  }

  return (
    <Card className="story-card" accentLeft>
      <div className="story-card-meta">
        <span className="story-card-pseudonym">{story.pseudonym}</span>
        {story.dayIndex != null ? (
          <span className="story-card-day">· Day {story.dayIndex + 1}</span>
        ) : null}
      </div>
      <p className="story-card-body">"{story.body}"</p>
      <div className="story-card-actions">
        <button
          type="button"
          className={`story-react-btn${bumped ? ' story-react-btn-on' : ''}`}
          onClick={() => void react()}
          aria-pressed={bumped}
          aria-label={bumped ? 'Reaction sent' : 'Send a heart'}
        >
          <Icon icon={Heart} size={16} />
          <span>{reactions}</span>
        </button>
      </div>
    </Card>
  );
}
